import React, { useState, useEffect, useRef } from 'react';

/**
 * Lazy Image Component
 * Loads images only when they're in viewport using Intersection Observer
 * Includes loading state and fade-in animation
 */
const LazyImage = ({ src, alt, style, ...props }) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isInView, setIsInView] = useState(false);
  const imgRef = useRef();

  useEffect(() => {
    const currentImg = imgRef.current;
    
    // Create intersection observer
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observer.unobserve(entry.target);
          }
        });
      },
      {
        rootMargin: '50px', // Start loading 50px before image enters viewport
      }
    );

    if (currentImg) {
      observer.observe(currentImg);
    }

    return () => {
      if (currentImg) {
        observer.unobserve(currentImg);
      }
    };
  }, []);

  useEffect(() => {
    if (isInView && src) {
      // Load the image
      const img = new Image();
      img.crossOrigin = 'anonymous'; // Required for Shopify CDN
      img.referrerPolicy = 'no-referrer'; // Shopify CDN doesn't need referrer
      img.src = src;
      img.onload = () => {
        setImageSrc(src);
        setIsLoading(false);
      };
      img.onerror = () => {
        console.error('Image failed to load:', src);
        setIsLoading(false);
      };
    }
  }, [isInView, src]);

  return (
    <div
      ref={imgRef}
      style={{
        ...style,
        backgroundColor: isLoading ? '#f6f6f7' : 'transparent',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}
      {...props}
    >
      {isLoading && !imageSrc && (
        <div style={{
          position: 'absolute',
          width: '30px',
          height: '30px',
          border: '3px solid #e1e3e5',
          borderTop: '3px solid #2c6ecb',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
      )}
      
      {imageSrc && (
        <img
          src={imageSrc}
          alt={alt}
          crossOrigin="anonymous"
          referrerPolicy="no-referrer"
          style={{
            ...style,
            opacity: isLoading ? 0 : 1,
            transition: 'opacity 0.3s ease-in-out'
          }}
        />
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default LazyImage;
